import { STORAGE_KEYS } from './constants';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'wbraid',
  'gbraid',
  'msclkid',
  'fbclid',
  'ref',
] as const;

export type UtmParams = Partial<Record<typeof UTM_KEYS[number], string>>;

interface StoredUtm {
  params: UtmParams;
  landingPage: string;
  referrer: string;
  timestamp: number;
  deviceType: 'desktop' | 'mobile' | 'tablet';
}

function getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  const ua = navigator.userAgent;
  if (/iPad|Android(?!.*Mobile)|Tablet/i.test(ua)) return 'tablet';
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile/i.test(ua)) return 'mobile';
  return 'desktop';
}

function generateSessionId(): string {
  return 'sess_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

export function getSessionId(): string {
  let sid = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
  if (!sid) {
    sid = generateSessionId();
    localStorage.setItem(STORAGE_KEYS.SESSION_ID, sid);
  }
  return sid;
}

export function captureUtmParams(): void {
  const url = new URL(window.location.href);
  const params: UtmParams = {};
  let hasUtm = false;

  for (const key of UTM_KEYS) {
    const value = url.searchParams.get(key);
    if (value) {
      params[key as keyof UtmParams] = value;
      hasUtm = true;
    }
  }

  // If no UTMs in URL, check if we already have stored UTMs
  const existing = getStoredUtm();
  if (!hasUtm && existing) {
    return; // Keep existing attribution
  }

  // If we have new UTMs, or this is a direct visit with no prior attribution,
  // store current page as landing page
  const stored: StoredUtm = {
    params: hasUtm ? params : existing?.params ?? {},
    landingPage: window.location.href,
    referrer: document.referrer || '',
    timestamp: Date.now(),
    deviceType: getDeviceType(),
  };

  localStorage.setItem(STORAGE_KEYS.UTM_DATA, JSON.stringify(stored));
}

export function getStoredUtm(): StoredUtm | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.UTM_DATA);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredUtm;
    // Expire after 30 days
    if (Date.now() - parsed.timestamp > 30 * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(STORAGE_KEYS.UTM_DATA);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function getUtmParams(): UtmParams {
  const stored = getStoredUtm();
  return stored?.params ?? {};
}

export function getAttributionSummary(): {
  utm: UtmParams;
  sessionId: string;
  landingPage: string;
  referrer: string;
  deviceType: string;
} {
  const stored = getStoredUtm();
  return {
    utm: stored?.params ?? {},
    sessionId: getSessionId(),
    landingPage: stored?.landingPage ?? window.location.href,
    referrer: stored?.referrer ?? (document.referrer || ''),
    deviceType: stored?.deviceType ?? getDeviceType(),
  };
}

export function getReferralCode(): string | null {
  const stored = getStoredUtm();
  const ref = stored?.params?.ref;
  return ref ? ref.toUpperCase() : null;
}

export function clearReferralCode(): void {
  const stored = getStoredUtm();
  if (stored) {
    delete stored.params.ref;
    localStorage.setItem(STORAGE_KEYS.UTM_DATA, JSON.stringify(stored));
  }
}

export function buildTrackingPayload(): Record<string, any> {
  const attr = getAttributionSummary();
  return {
    session_id: attr.sessionId,
    utm_source: attr.utm.utm_source ?? '',
    utm_medium: attr.utm.utm_medium ?? '',
    utm_campaign: attr.utm.utm_campaign ?? '',
    utm_content: attr.utm.utm_content ?? '',
    utm_term: attr.utm.utm_term ?? '',
    gclid: attr.utm.gclid ?? '',
    fbclid: attr.utm.fbclid ?? '',
    wbraid: attr.utm.wbraid ?? '',
    referrer: attr.referrer,
    landing_page: attr.landingPage,
    device_type: attr.deviceType,
    whatsapp_ref: attr.utm.ref ?? '',
    referral_code: attr.utm.ref ?? '',
  };
}

export function clearUtmData(): void {
  localStorage.removeItem(STORAGE_KEYS.UTM_DATA);
}
